import Typography from '@mui/material/Typography';
import { useSmartListById } from 'features/smart-lists';
import React, { useCallback } from 'react';
import { useParams } from 'react-router';
import {
  FilterMinimalStyleCircle16,
  IconFormIcons,
  IconFormNames,
} from 'shared/assets/icons';
import { TopBarBase } from 'shared/components/connected/top-bar-base';
import { Icon } from 'shared/components/ui/icon';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { SmartListFormDialog } from 'shared/dialogs/smart-list-form-dialog';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { FilterDocument } from 'shared/types/filter-document';

import { Interactions } from './interactions';
import * as Styled from './page-top-bar.style';

type PageTopBarProps = {
  showCompleted: boolean;
  onToggleShowCompleted: () => void;
  filterDocument: FilterDocument;
  defaultFilterDocument: FilterDocument;
  onUpdateFilterDocument: (filterDocument: FilterDocument) => void;
  onResetFilter: () => void;
  onUpdateSmartList: () => void;
};

export const PageTopBar: React.FC<PageTopBarProps> = ({
  showCompleted,
  onToggleShowCompleted,
  filterDocument,
  defaultFilterDocument,
  onUpdateFilterDocument,
  onResetFilter,
  onUpdateSmartList,
}) => {
  const { id } = useParams();
  const smartList = useSmartListById(id);
  const openPremiumDialog = useOpenPremiumDialog();
  const {
    menuOpen: editDialogOpen,
    openMenu: openEditDialog,
    closeMenu: closeEditDialog,
  } = useOpenMenu();

  const onEdit = useCallback(() => {
    if (smartList?.frozenAt) {
      openPremiumDialog();
      return;
    }
    openEditDialog();
  }, [smartList?.frozenAt, openEditDialog, openPremiumDialog]);

  const _onUpdateSmartList = useCallback(() => {
    if (smartList?.frozenAt) {
      openPremiumDialog();
      return;
    }
    onUpdateSmartList();
  }, [onUpdateSmartList, openPremiumDialog, smartList?.frozenAt]);

  if (!smartList) {
    return null;
  }

  const iconName = smartList.icon as IconFormNames;
  const IconComponent = IconFormIcons[iconName] ?? FilterMinimalStyleCircle16;

  return (
    <TopBarBase smallLeftPadding>
      <Styled.Container>
        <Styled.Content>
          <Styled.IconContainer $color={smartList.color}>
            <Icon icon={IconComponent} />
          </Styled.IconContainer>
          <Typography variant="h5" component="h1" color="text.primary">
            {smartList.name}
          </Typography>
        </Styled.Content>

        <Interactions
          smartList={smartList}
          showCompleted={showCompleted}
          onToggleShowCompleted={onToggleShowCompleted}
          onEdit={onEdit}
          filterDocument={filterDocument}
          defaultFilterDocument={defaultFilterDocument}
          onUpdateFilterDocument={onUpdateFilterDocument}
          onResetFilter={onResetFilter}
          onUpdateSmartList={_onUpdateSmartList}
        />

        <SmartListFormDialog
          open={editDialogOpen}
          onClose={closeEditDialog}
          smartList={smartList}
        />
      </Styled.Container>
    </TopBarBase>
  );
};
